import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';

/** The link-sized actions are about 20px tall; the slop brings them to the minimum target. */
const LINK_HIT_SLOP = getAccessibleHitSlop(20);

type SendStatusLineProps = {
  status: 'sent' | 'pending' | 'failed';
  /** What a sent or pending message shows under it, such as its time or "Sending…". */
  caption: string;
  /** Why a failed message was refused, when the reader can act on it; shown after "Not sent". */
  failureReason?: string;
  /** Absent when retrying would only be refused again. */
  onRetry?: () => void;
  onDiscard?: () => void;
};

/**
 * The line under the reader's own message that says where its send stands.
 * Support threads and inquiry chat draw the same one, so a send reads the
 * same in both. A failed send says so in words, not by color alone, and
 * offers Delete, and Retry unless the refusal would only repeat.
 */
export function SendStatusLine({
  status,
  caption,
  failureReason,
  onRetry,
  onDiscard,
}: SendStatusLineProps) {
  if (status !== 'failed') {
    return <Text className="mt-1 px-1 text-xs text-typography-700">{caption}</Text>;
  }

  return (
    <HStack className="mt-1 items-center gap-3 px-1">
      <Text className="shrink text-xs text-error-700" accessibilityLiveRegion="polite">
        {failureReason ? `Not sent. ${failureReason}` : 'Not sent'}
      </Text>
      {onRetry ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          hitSlop={LINK_HIT_SLOP}
          onPress={onRetry}>
          <ButtonText className="text-xs text-primary-700">Retry</ButtonText>
        </Button>
      ) : null}
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0"
        hitSlop={LINK_HIT_SLOP}
        onPress={onDiscard}>
        <ButtonText className="text-xs text-typography-700">Delete</ButtonText>
      </Button>
    </HStack>
  );
}
